/**
 * Turns a month plus the days that have games into the exact 7-column grid
 * the calendar renders, including the leading and trailing blanks.
 */
import {
  dayOfMonth,
  firstDayOfMonth,
  lastDayOfMonth,
  shiftDay,
} from "./calendarDay";

/** One cell. A `day` of null is a leading or trailing blank, not a date. */
export type typeMonthCell = {
  day: string | null;
  dayOfMonth: number | null;
  hasGames: boolean;
  isToday: boolean;
  isSelected: boolean;
  /** Per-day game count. ALWAYS null today: POST /event/days-with-games-list
   *  answers days only, with no counts, and POST /event/calendar-get does not
   *  exist yet (ISSUE-165). The field is here so wiring that endpoint later
   *  changes no component. */
  gameCount: number | null;
};

export const WEEKDAY_HEADINGS = [
  "Su",
  "Mo",
  "Tu",
  "We",
  "Th",
  "Fr",
  "Sa",
] as const;

function weekdayIndex(day: string): number {
  const [yearPart, monthPart, dayPart] = day.split("-");
  const date = new Date(
    Date.UTC(Number(yearPart), Number(monthPart) - 1, Number(dayPart)),
  );
  return date.getUTCDay();
}

export function buildMonthGrid(input: {
  /** Any "YYYY-MM-DD" inside the month to draw. */
  monthAnchorDay: string;
  daysWithGames: readonly string[];
  todayDay: string;
  selectedDay: string;
  /** Inclusive last day of a selected range, "YYYY-MM-DD". Left out, only
   *  `selectedDay` is selected. A value before `selectedDay` is ignored.
   *  ISSUE-958: a week-based league selects a whole week. */
  selectedLastDay?: string | undefined;
}): typeMonthCell[] {
  const first = firstDayOfMonth(input.monthAnchorDay);
  const last = lastDayOfMonth(input.monthAnchorDay);
  const gameDays = new Set(input.daysWithGames);
  const lastSelectedDay =
    input.selectedLastDay !== undefined &&
    input.selectedLastDay > input.selectedDay
      ? input.selectedLastDay
      : input.selectedDay;

  const cells: typeMonthCell[] = [];

  const leadingBlanks = weekdayIndex(first);
  for (let i = 0; i < leadingBlanks; i += 1) {
    cells.push({
      day: null,
      dayOfMonth: null,
      hasGames: false,
      isToday: false,
      isSelected: false,
      gameCount: null,
    });
  }

  let cursor = first;
  while (cursor <= last) {
    cells.push({
      day: cursor,
      dayOfMonth: dayOfMonth(cursor),
      hasGames: gameDays.has(cursor),
      isToday: cursor === input.todayDay,
      isSelected: input.selectedDay <= cursor && cursor <= lastSelectedDay,
      gameCount: null,
    });
    if (cursor === last) break;
    cursor = shiftDay({ day: cursor, deltaDays: 1 });
  }

  while (cells.length % 7 !== 0) {
    cells.push({
      day: null,
      dayOfMonth: null,
      hasGames: false,
      isToday: false,
      isSelected: false,
      gameCount: null,
    });
  }

  return cells;
}
